import { normalizeFontWeight } from './direct-sale-fonts.js';

/**
 * The Seller Fonts: the families the Seller Creator's font picker offers,
 * each in regular and bold, upright and (where the family has one) italic.
 * The picker writes `value` into a text object's fontFamily; the bold and
 * italic toggles write `bold` / `italic`. A new text starts in Arial.
 *
 * The API renders the print file from a bundled static TrueType instance
 * per face (apps/api/assets/seller-fonts) and fails the freeze for a face
 * this list does not offer instead of rendering it in a fallback. A family
 * enters this list only together with its TTFs in the API.
 */

export interface SellerFont {
  /** Family name, as the picker shows it and the server registers it. */
  name: string;
  /** CSS font-family value the picker writes into the text object. */
  value: string;
  /** Font category for filtering. */
  category: 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace';
  /** Whether the family has an italic cut; without one italic is slanted. */
  italic: boolean;
}

export const SELLER_FONTS: readonly [SellerFont, ...SellerFont[]] = [
  {
    name: 'Roboto',
    value: 'Roboto, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Open Sans',
    value: 'Open Sans, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Lato',
    value: 'Lato, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Montserrat',
    value: 'Montserrat, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Poppins',
    value: 'Poppins, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Oswald',
    value: 'Oswald, sans-serif',
    category: 'display',
    italic: false,
  },
  {
    name: 'Raleway',
    value: 'Raleway, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Ubuntu',
    value: 'Ubuntu, sans-serif',
    category: 'sans-serif',
    italic: true,
  },
  {
    name: 'Playfair Display',
    value: 'Playfair Display, serif',
    category: 'serif',
    italic: true,
  },
  {
    name: 'Merriweather',
    value: 'Merriweather, serif',
    category: 'serif',
    italic: true,
  },
];

/**
 * The family of a new text in the Seller Creator. Not in the picker; the
 * server renders it from a metric-compatible substitute (Liberation Sans).
 */
export const SELLER_DEFAULT_TEXT_FONT_FAMILY = 'Arial';

/** The weights the bold toggle switches between. */
export type SellerFontWeight = 400 | 700;
export type SellerFontStyle = 'normal' | 'italic';

/** One static face: what the server registers and renders. */
export interface SellerFontFace {
  fontFamily: string;
  fontWeight: SellerFontWeight;
  fontStyle: SellerFontStyle;
}

/** Every family a seller text may use: the picker's and the default. */
const SELLER_TEXT_FAMILIES: ReadonlyArray<{ name: string; italic: boolean }> = [
  ...SELLER_FONTS,
  { name: SELLER_DEFAULT_TEXT_FONT_FAMILY, italic: true },
];

/** Every face the server has to register, in list order. */
export const SELLER_FONT_FACES: readonly SellerFontFace[] =
  SELLER_TEXT_FAMILIES.flatMap((family) =>
    ([400, 700] as const).flatMap((fontWeight) =>
      (family.italic
        ? (['normal', 'italic'] as const)
        : (['normal'] as const)
      ).map((fontStyle) => ({ fontFamily: family.name, fontWeight, fontStyle }))
    )
  );

/**
 * The family a CSS font-family list asks for first, without quotes, or
 * null when it names none. The picker writes "Montserrat, sans-serif".
 */
export function primaryFontFamily(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const first = value
    .split(',')[0]
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .trim();
  return first === '' ? null : first;
}

/**
 * The face the server renders a seller text object from, or null when the
 * list does not offer its family or weight. A family without an italic
 * cut answers with its upright face, which the renderer slants as the
 * browser does.
 */
export function findSellerFontFace(
  fontFamily: unknown,
  fontWeight: unknown,
  fontStyle: unknown
): SellerFontFace | null {
  const name = primaryFontFamily(fontFamily)?.toLowerCase();
  if (!name) return null;
  const family = SELLER_TEXT_FAMILIES.find(
    (candidate) => candidate.name.toLowerCase() === name
  );
  const weight = normalizeFontWeight(fontWeight);
  if (!family || (weight !== 400 && weight !== 700)) return null;
  const slanted = fontStyle === 'italic' || fontStyle === 'oblique';
  return {
    fontFamily: family.name,
    fontWeight: weight,
    fontStyle: slanted && family.italic ? 'italic' : 'normal',
  };
}
